import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PosService } from "../../services";
import { useAuth } from "../../store/AuthContext";
import { useCashierSessionGate } from "../../hooks/useCashierSessionGate";
import { currency } from "../../utils/format";
import { formatSriLankaDate } from "../../utils/sriLankaTime";
import { isSalesBlockedNow } from "../../utils/businessDay";
import { calculateServiceCharge } from "../../utils/saleFinancials";
import { isCigaretteBarProduct } from "../../utils/barProducts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Search,
  Plus,
  Minus,
  Trash2,
  ShoppingCart,
  X,
  Wine,
  Utensils,
  Coffee,
  CreditCard,
  Banknote,
  Lock,
} from "lucide-react";
import { toast } from "sonner";

const createClientRequestId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const BOTTLE_SALE_MODES = {
  FULL_BOTTLE: "FULL_BOTTLE",
  MEASURE_180ML: "MEASURE_180ML",
  MEASURE_90ML: "MEASURE_90ML",
};

const RESTAURANT_ITEM_SECTIONS = {
  FOOD: "FOOD",
  BEVERAGE: "BEVERAGE",
  GOODS: "GOODS",
};

const POS_TABS = {
  BAR: "BOTTLE",
  BEVERAGE: "BEVERAGE",
  FOOD: "FOOD",
};

const PAYMENT_MODES = {
  CASH: "CASH",
  CARD: "CARD",
  SPLIT: "SPLIT",
};

const resolveRestaurantItemType = (product) => {
  if (product.itemSection === RESTAURANT_ITEM_SECTIONS.BEVERAGE) {
    return "BEVERAGE";
  }

  if (product.itemSection === RESTAURANT_ITEM_SECTIONS.GOODS) {
    return "GOODS";
  }

  return "FOOD";
};

const formatContainerAndVolume = (product) => {
  if (isCigaretteBarProduct(product)) return "Cigarette";
  const container = product.bottleSize === "TIN" ? "Tin" : "Bottle";

  const volume = product.volumeMl ? `${product.volumeMl} ML` : "";

  return [container, volume].filter(Boolean).join(" · ");
};

const roundMoney = (value) => Math.round(Number(value || 0) * 100) / 100;

const normalizeBarcode = (value) => String(value || "").trim();

const normalizeSearchText = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/\brs\.?/g, " ")
    .replace(/\blkr\b/g, " ")
    .replace(/[,]/g, "")
    .replace(/[^a-z0-9.]+/g, " ")
    .trim();

const getPriceSearchValues = (value) => {
  if (value === null || value === undefined || value === "") {
    return [];
  }

  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return [];
  }

  return [
    numericValue,
    numericValue.toFixed(2),
    currency(numericValue),
  ];
};

const getPaymentModeLabel = (paymentMode) => {
  if (paymentMode === PAYMENT_MODES.CARD) {
    return "Card";
  }

  if (paymentMode === PAYMENT_MODES.SPLIT) {
    return "Split";
  }

  return "Cash";
};

export const SharedPOSPage = ({ config }) => {
  const sessionPath = config.cashSessionRoute;
  const { user } = useAuth();
  const navigate = useNavigate();
  const { session, loading: sessionLoading } = useCashierSessionGate({ sessionPath });
  const draftStorageKey = user?.userId ? `bms_pos_draft_${user.userId}_${sessionPath}` : null;
  const [savedDraft] = useState(() => {
    try { return draftStorageKey ? JSON.parse(sessionStorage.getItem(draftStorageKey) || "null") : null; }
    catch { return null; }
  });
  const hasRestorableCart = Boolean(savedDraft?.cart?.length);

  const clientRequestIdRef = useRef(savedDraft?.clientRequestId || createClientRequestId());
  const barcodeBufferRef = useRef("");
  const barcodeTimerRef = useRef(null);

  const [bottles, setBottles] = useState([]);
  const [bites, setBites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [tab, setTab] = useState(config.initialSection);
  const [search, setSearch] = useState("");
  const [cart, setCart] = useState(savedDraft?.cart || []);
  const [discount, setDiscount] = useState(savedDraft?.discount || 0);
  const [serviceEnabled, setServiceEnabled] = useState(savedDraft?.serviceEnabled || false);
  const [tax, setTax] = useState(savedDraft?.tax || 0);
  const [cashAmount, setCashAmount] = useState(savedDraft?.cashAmount || 0);
  const [cardAmount, setCardAmount] = useState(savedDraft?.cardAmount || 0);
  const [paymentMode, setPaymentMode] = useState(savedDraft?.paymentMode || PAYMENT_MODES.CASH);
  const [paymentRef, setPaymentRef] = useState(savedDraft?.paymentRef || "");
  const [paymentTouched, setPaymentTouched] = useState(savedDraft?.paymentTouched || false);
  const [submitting, setSubmitting] = useState(false);
  const [salesBlocked, setSalesBlocked] = useState(isSalesBlockedNow());

  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentDraftMode, setPaymentDraftMode] = useState(PAYMENT_MODES.CASH);
  const [paymentDraftCashAmount, setPaymentDraftCashAmount] = useState(0);
  const [paymentDraftCardAmount, setPaymentDraftCardAmount] = useState(0);
  const [paymentDraftRef, setPaymentDraftRef] = useState("");

  const [measureBottleModal, setMeasureBottleModal] = useState(null);
  const [customItemModalOpen, setCustomItemModalOpen] = useState(false);
  const [customItemForm, setCustomItemForm] = useState({
    itemSection: RESTAURANT_ITEM_SECTIONS.FOOD,
    itemName: "",
    unitPrice: "",
    quantity: 1,
  });

  useEffect(() => {
    if (hasRestorableCart) toast.info("Restored this account's unsaved sale.");
  }, [hasRestorableCart]);

  useLayoutEffect(() => {
    if (!draftStorageKey) return;
    if (!cart.length) {
      sessionStorage.removeItem(draftStorageKey);
      return;
    }
    sessionStorage.setItem(draftStorageKey, JSON.stringify({
      cart, discount, serviceEnabled, tax, cashAmount, cardAmount, paymentMode, paymentRef, paymentTouched,
      clientRequestId: clientRequestIdRef.current,
    }));
  }, [draftStorageKey, cart, discount, serviceEnabled, tax, cashAmount, cardAmount, paymentMode, paymentRef, paymentTouched]);

  const loadCatalog = async () => {
    setLoading(true);
    setCatalogError(false);
    try {
      const [b, bi] = !config.bottlesEnabled
        ? [[], await PosService.bites()]
        : await Promise.all([PosService.bottles(), PosService.bites()]);
      setBottles(b);
      setBites(bi);
    } catch {
      setCatalogError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCatalog();
  }, [config]);

  useEffect(() => {
    const checkBlocked = () => setSalesBlocked(isSalesBlockedNow());

    checkBlocked();

    const interval = setInterval(checkBlocked, 30000);

    return () => clearInterval(interval);
  }, []);

  const filteredItems = useMemo(() => {
    const q = normalizeSearchText(search);

    const decorateBottle = (product) => ({
      ...product,
      __posType: "BOTTLE",
      __posTab: POS_TABS.BAR,
      __posGroupLabel: product.barProductType === "CIGARETTE" ? "Cigarettes" : "Liquor",
    });

    const decorateRestaurantItem = (product) => {
      const section = product.itemSection || RESTAURANT_ITEM_SECTIONS.FOOD;

      return {
        ...product,
        __posType: "RESTAURANT",
        __posTab:
          config.sections.find((entry) => entry.productSections.includes(section))?.id || POS_TABS.FOOD,
        __posGroupLabel: config.sections.find((entry) => entry.productSections.includes(section))?.label || "Food & Bites",
      };
    };

    const decoratedBottles = bottles.map(decorateBottle);
    const decoratedRestaurantItems = bites.map(decorateRestaurantItem);

    const stockSortedBottles = (items) =>
      [...items].sort((a, b) => {
        const aSealedQuantity = Number(a.availableQuantity || 0);
        const bSealedQuantity = Number(b.availableQuantity || 0);

        const aOpenedUnits = Number(a.availableMeasureUnits || 0);
        const bOpenedUnits = Number(b.availableMeasureUnits || 0);

        const aHasStock = a.barProductType !== "CIGARETTE" && a.allowMeasureSale
          ? aSealedQuantity > 0 || aOpenedUnits > 0
          : aSealedQuantity > 0;

        const bHasStock = b.barProductType !== "CIGARETTE" && b.allowMeasureSale
          ? bSealedQuantity > 0 || bOpenedUnits > 0
          : bSealedQuantity > 0;

        if (aHasStock !== bHasStock) {
          return aHasStock ? -1 : 1;
        }

        const aTotalStockValue = aSealedQuantity + aOpenedUnits;
        const bTotalStockValue = bSealedQuantity + bOpenedUnits;

        return bTotalStockValue - aTotalStockValue;
      });

    const productMatchesSearch = (product) => {
      if (!q) {
        return true;
      }

      const searchableValues =
        product.__posType === "BOTTLE"
          ? [
              "bar",
              product.barProductType === "CIGARETTE" ? "cigarette" : "liquor bottle",
              product.barcode,
              product.bottleName,
              product.bottleTypeName,
              product.companyName,
              product.bottleSize,
              product.volumeMl,
              formatContainerAndVolume(product),
              product.availableQuantity,
              product.availableMeasureUnits,
              product.lowStockLevel,
              ...getPriceSearchValues(product.sellingPrice),
              ...getPriceSearchValues(product.measure180Price),
              ...getPriceSearchValues(product.measure90Price),
              ...getPriceSearchValues(product.bottleReturnCredit),
            ]
          : [
              "restaurant",
              product.__posGroupLabel,
              product.barcode,
              product.biteName,
              product.biteCategoryName,
              product.itemSection,
              product.stockTracked ? "stock tracked" : "custom stock optional",
              product.availableQuantity,
              product.lowStockLevel,
              ...getPriceSearchValues(product.sellingPrice),
            ];

      return searchableValues
        .filter((value) => value !== null && value !== undefined)
        .some((value) => normalizeSearchText(value).includes(q));
    };

    /*
     * When the cashier searches, search across all POS product groups:
     * Bar bottles, restaurant beverages, food/goods/bites, prices and codes.
     * The cashier does not need to switch tabs first.
     */
    if (q) {
      return [
        ...(config.bottlesEnabled ? stockSortedBottles(decoratedBottles) : []),
        ...decoratedRestaurantItems,
      ].filter(productMatchesSearch);
    }

    if (config.bottlesEnabled && tab === POS_TABS.BAR) {
      return stockSortedBottles(decoratedBottles);
    }

    const activeSection = config.sections.find((section) => section.id === tab);
    return decoratedRestaurantItems.filter((item) => {
      const section = item.itemSection || RESTAURANT_ITEM_SECTIONS.FOOD;
      return activeSection?.productSections.includes(section);
    });
  }, [tab, bottles, bites, search, config]);

  const getTotalMeasureUnits = (product) => {
    const availableOpenedUnits = Number(product.availableMeasureUnits || 0);

    const availableSealedUnits = Number(product.availableQuantity || 0) * 8;

    return availableOpenedUnits + availableSealedUnits;
  };

  const getMeasuredMaximumQuantity = (product, saleMode) => {
    const totalUnits = getTotalMeasureUnits(product);

    if (saleMode === BOTTLE_SALE_MODES.MEASURE_180ML) {
      return Math.floor(totalUnits / 2);
    }

    if (saleMode === BOTTLE_SALE_MODES.MEASURE_90ML) {
      return totalUnits;
    }

    return Number(product.availableQuantity || 0);
  };

  const hasAnyBottleStock = (product) => {
    const sealedQuantity = Number(product.availableQuantity || 0);

    if (product.barProductType === "CIGARETTE" || !product.allowMeasureSale) {
      return sealedQuantity > 0;
    }

    const openedUnits = Number(product.availableMeasureUnits || 0);

    return sealedQuantity > 0 || openedUnits > 0;
  };

  const isStockTrackedRestaurantItem = (product) =>
    Boolean(product.stockTracked);

  const getRestaurantAvailableQuantity = (product) =>
    Number(product.availableQuantity || 0);

  const isRestaurantOutOfStock = (product) =>
    isStockTrackedRestaurantItem(product) &&
    getRestaurantAvailableQuantity(product) <= 0;

  const isRestaurantLowStock = (product) => {
    if (!isStockTrackedRestaurantItem(product)) {
      return false;
    }

    const availableQuantity = getRestaurantAvailableQuantity(product);
    const lowStockLevel = Number(product.lowStockLevel || 0);

    return availableQuantity > 0 && availableQuantity <= lowStockLevel;
  };

  const handleBottleClick = (product) => {
    if (product.barProductType !== "CIGARETTE" && product.allowMeasureSale) {
      setMeasureBottleModal(product);
      return;
    }

    addItem(product, "BOTTLE", BOTTLE_SALE_MODES.FULL_BOTTLE);
  };

  const handleProductSelection = (product, out) => {
    if (out) {
      return;
    }

    const productTab = product.__posTab || tab;

    if (productTab !== tab) {
      setTab(productTab);
    }

    if (search.trim()) {
      setSearch("");
    }

    if (product.__posType === "BOTTLE") {
      handleBottleClick(product);
      return;
    }

    addItem(product, resolveRestaurantItemType(product));
  };

  const openCustomItemModal = () => {
    const defaultSection =
      config.sections.find((section) => section.id === tab)?.productSections[0] ||
      RESTAURANT_ITEM_SECTIONS.FOOD;

    setCustomItemForm({
      itemSection: defaultSection,
      itemName: "",
      unitPrice: "",
      quantity: 1,
    });

    setCustomItemModalOpen(true);
  };

  const updateCustomItemForm = (field, value) => {
    setCustomItemForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const addCustomRestaurantItem = () => {
    if (salesBlocked) {
      return toast.error(
        "Sales are closed for the business day. Please close your cash session."
      );
    }

    const itemName = customItemForm.itemName.trim();
    const unitPrice = Number(customItemForm.unitPrice || 0);
    const quantity = Math.max(
      1,
      Math.floor(Number(customItemForm.quantity || 1))
    );

    if (!itemName) {
      return toast.error("Custom item name is required");
    }

    if (unitPrice <= 0) {
      return toast.error("Custom item price must be greater than zero");
    }

    const itemSection =
      customItemForm.itemSection || RESTAURANT_ITEM_SECTIONS.FOOD;

    const itemType =
      itemSection === RESTAURANT_ITEM_SECTIONS.BEVERAGE
        ? "BEVERAGE"
        : itemSection === RESTAURANT_ITEM_SECTIONS.GOODS
          ? "GOODS"
          : "FOOD";

    const key = `CUSTOM-RESTAURANT-${Date.now()}`;

    setCart([
      ...cart,
      {
        key,
        itemType,
        itemId: null,
        name: itemName,
        unitPrice,
        quantity,
        max: 9999,
        customItem: true,
        customItemName: itemName,
        customUnitPrice: unitPrice,
        isReturnableBottle: false,
        bottleReturnCredit: 0,
        returnedBottleQuantity: 0,
        bottleReturned: false,
        bottleSaleMode: null,
      },
    ]);

    setCustomItemModalOpen(false);
    setCustomItemForm({
      itemSection: RESTAURANT_ITEM_SECTIONS.FOOD,
      itemName: "",
      unitPrice: "",
      quantity: 1,
    });
  };

  const addItem = (
    product,
    itemType = tab,
    bottleSaleMode = BOTTLE_SALE_MODES.FULL_BOTTLE
  ) => {
    if (salesBlocked) {
      return toast.error(
        "Sales are closed for the business day. Please close your cash session."
      );
    }

    if (itemType === "BOTTLE") {
      const isMeasuredSale =
        bottleSaleMode !== BOTTLE_SALE_MODES.FULL_BOTTLE;

      const maximumQuantity = isMeasuredSale
        ? getMeasuredMaximumQuantity(product, bottleSaleMode)
        : Number(product.availableQuantity || 0);

      if (maximumQuantity <= 0) {
        return toast.error(
          isMeasuredSale ? "No measured stock is available" : "Out of stock"
        );
      }

      let unitPrice = Number(product.sellingPrice);

      if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_180ML) {
        unitPrice = Number(product.measure180Price);
      } else if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_90ML) {
        unitPrice = Number(product.measure90Price);
      }

      let key;

      if (isMeasuredSale) {
        key = `BOTTLE-${product.bottleId}-${bottleSaleMode}`;
      } else {
        key = `BOTTLE-${product.bottleId}-FULL_BOTTLE`;
      }

      const found = cart.find((item) => item.key === key);

      if (found) {
        if (found.quantity >= maximumQuantity) {
          return toast.error("Reached available stock");
        }

        setCart(
          cart.map((item) =>
            item.key === key
              ? {
                  ...item,
                  quantity: item.quantity + 1,
                  max: maximumQuantity,
                }
              : item
          )
        );
      } else {
        let measuredLabel = "";

        if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_180ML) {
          measuredLabel = " - 180 ml";
        } else if (bottleSaleMode === BOTTLE_SALE_MODES.MEASURE_90ML) {
          measuredLabel = " - 90 ml";
        }

        setCart([
          ...cart,
          {
            key,
            itemType: "BOTTLE",
            barProductType: product.barProductType || "LIQUOR",
            itemId: product.bottleId,
            name: isCigaretteBarProduct(product)
              ? product.bottleName
              : `${product.bottleName} ${formatContainerAndVolume(product)}${measuredLabel}`,
            unitPrice,
            quantity: 1,
            max: maximumQuantity,
            isReturnableBottle: !isCigaretteBarProduct(product) && Boolean(product.isReturnableBottle),
            bottleReturnCredit: isCigaretteBarProduct(product) ? 0 : Number(product.bottleReturnCredit || 0),
            returnedBottleQuantity: 0,
            bottleReturned: false,
            bottleSaleMode,
          },
        ]);
      }

      setMeasureBottleModal(null);
      return;
    }

    const restaurantItemType =
      itemType === "BITE" ? resolveRestaurantItemType(product) : itemType;

    const isStockTracked = isStockTrackedRestaurantItem(product);

    const maximumQuantity = isStockTracked
      ? getRestaurantAvailableQuantity(product)
      : 9999;

    if (maximumQuantity <= 0) {
      return toast.error("Out of stock");
    }

    const key = `RESTAURANT-${product.biteId}`;
    const found = cart.find((item) => item.key === key);

    if (found) {
      if (found.quantity >= maximumQuantity) {
        return toast.error("Reached available stock");
      }

      setCart(
        cart.map((item) =>
          item.key === key
            ? {
                ...item,
                quantity: item.quantity + 1,
                itemType: restaurantItemType,
                max: maximumQuantity,
              }
            : item
        )
      );
    } else {
      setCart([
        ...cart,
        {
          key,
          itemType: restaurantItemType,
          itemId: product.biteId,
          name: product.biteName,
          unitPrice: Number(product.sellingPrice),
          quantity: 1,
          max: maximumQuantity,
          isReturnableBottle: false,
          bottleReturnCredit: 0,
          returnedBottleQuantity: 0,
          bottleReturned: false,
          bottleSaleMode: null,
        },
      ]);
    }
  };

  const incr = (key) => {
    const found = cart.find((item) => item.key === key);

    if (found && Number(found.quantity || 0) >= Number(found.max || 9999)) {
      return toast.error("Reached available stock");
    }

    setCart(
      cart.map((item) =>
        item.key === key
          ? {
              ...item,
              quantity: item.quantity + 1,
            }
          : item
      )
    );
  };

  const decr = (key) =>
    setCart(
      cart.map((item) =>
        item.key === key
          ? {
              ...item,
              quantity: Math.max(1, item.quantity - 1),
            }
          : item
      )
    );

  const updateReturnedBottleQuantity = (key, value) => {
    setCart(
      cart.map((item) => {
        if (item.key !== key) {
          return item;
        }

        const requestedQuantity = Number(value || 0);

        const returnedBottleQuantity = Math.max(
          0,
          Math.floor(requestedQuantity)
        );

        return {
          ...item,
          returnedBottleQuantity,
          bottleReturned: returnedBottleQuantity > 0,
        };
      })
    );
  };

  const adjustReturnedBottleQuantity = (key, amount) => {
    const item = cart.find((cartItem) => cartItem.key === key);

    if (!item) {
      return;
    }

    const nextReturnedBottleQuantity =
      Number(item.returnedBottleQuantity || 0) + Number(amount || 0);

    updateReturnedBottleQuantity(key, nextReturnedBottleQuantity);
  };

  const remove = (key) => setCart(cart.filter((item) => item.key !== key));

  const clear = () => {
    if (draftStorageKey) sessionStorage.removeItem(draftStorageKey);
    setCart([]);
    setDiscount(0);
    setServiceEnabled(false);
    setTax(0);
    setCashAmount(0);
    setCardAmount(0);
    setPaymentMode(PAYMENT_MODES.CASH);
    setPaymentRef("");
    setPaymentTouched(false);
    setPaymentModalOpen(false);
    setMeasureBottleModal(null);
    setCustomItemModalOpen(false);
    setCustomItemForm({
      itemSection: RESTAURANT_ITEM_SECTIONS.FOOD,
      itemName: "",
      unitPrice: "",
      quantity: 1,
    });
  };

  const getCartItemAmounts = (item) => {
    const quantity = Number(item.quantity || 0);
    const unitPrice = Number(item.unitPrice || 0);

    const returnedBottleQuantity =
      item.itemType === "BOTTLE" && item.barProductType !== "CIGARETTE" &&
      item.isReturnableBottle
        ? Number(item.returnedBottleQuantity || 0)
        : 0;

    const bottleReturnCredit = Number(item.bottleReturnCredit || 0);

    const grossAmount = quantity * unitPrice;

    const returnCreditAmount = returnedBottleQuantity * bottleReturnCredit;

    return {
      grossAmount,
      returnCreditAmount,
      netAmount: grossAmount - returnCreditAmount,
    };
  };

  const subTotal = cart.reduce(
    (sum, item) => sum + getCartItemAmounts(item).netAmount,
    0
  );

  const barSubtotal = cart.reduce((sum, item) => sum +
    (item.itemType === "BOTTLE" ? getCartItemAmounts(item).netAmount : 0), 0);
  const restaurantSubtotal = roundMoney(subTotal - barSubtotal);
  const appliedDiscount = Number(discount || 0);
  const serviceAmount = calculateServiceCharge(restaurantSubtotal, appliedDiscount, config.bottlesEnabled, serviceEnabled);
  const appliedTax = config.bottlesEnabled && barSubtotal <= 0 ? 0 : Number(tax || 0);
  const grandTotal = roundMoney(subTotal - appliedDiscount + serviceAmount + appliedTax);

  const cashPaymentAmount = roundMoney(cashAmount);
  const cardPaymentAmount = roundMoney(cardAmount);
  const totalPaidAmount = roundMoney(cashPaymentAmount + cardPaymentAmount);
  const balanceAmount = roundMoney(grandTotal - totalPaidAmount);
  const isPaymentBalanced = Math.abs(balanceAmount) < 0.01;

  const draftCashPaymentAmount = roundMoney(paymentDraftCashAmount);
  const draftCardPaymentAmount = roundMoney(paymentDraftCardAmount);
  const draftTotalPaidAmount = roundMoney(
    draftCashPaymentAmount + draftCardPaymentAmount
  );
  const draftBalanceAmount = roundMoney(grandTotal - draftTotalPaidAmount);
  const isDraftPaymentBalanced = Math.abs(draftBalanceAmount) < 0.01;

  useEffect(() => {
    if (paymentMode === PAYMENT_MODES.CASH) {
      setCashAmount(grandTotal);
      setCardAmount(0);
      setPaymentRef("");
      return;
    }

    if (paymentMode === PAYMENT_MODES.CARD) {
      setCashAmount(0);
      setCardAmount(grandTotal);
      return;
    }

    if (!paymentTouched) {
      setCashAmount(grandTotal);
      setCardAmount(0);
    }
  }, [grandTotal, paymentMode, paymentTouched]);

  const openPaymentModal = () => {
    setPaymentDraftMode(paymentMode);
    setPaymentDraftCashAmount(cashPaymentAmount);
    setPaymentDraftCardAmount(cardPaymentAmount);
    setPaymentDraftRef(paymentRef || "");
    setPaymentModalOpen(true);
  };

  const selectDraftPaymentMode = (nextPaymentMode) => {
    setPaymentDraftMode(nextPaymentMode);

    if (nextPaymentMode === PAYMENT_MODES.CASH) {
      setPaymentDraftCashAmount(grandTotal);
      setPaymentDraftCardAmount(0);
      setPaymentDraftRef("");
      return;
    }

    if (nextPaymentMode === PAYMENT_MODES.CARD) {
      setPaymentDraftCashAmount(0);
      setPaymentDraftCardAmount(grandTotal);
      return;
    }

    if (draftTotalPaidAmount <= 0) {
      setPaymentDraftCashAmount(grandTotal);
      setPaymentDraftCardAmount(0);
    }
  };

  const updateDraftCashAmount = (value) => {
    setPaymentDraftCashAmount(value === "" ? "" : roundMoney(Math.max(0, Number(value))));
  };

  const updateDraftCardAmount = (value) => {
    setPaymentDraftCardAmount(value === "" ? "" : roundMoney(Math.max(0, Number(value))));
  };

  const applyPaymentModal = () => {
    if (grandTotal <= 0) {
      setPaymentModalOpen(false);
      return;
    }

    if (paymentDraftMode === PAYMENT_MODES.SPLIT && !isDraftPaymentBalanced) {
      return toast.error("Payment total must be equal to grand total");
    }

    if (paymentDraftMode === PAYMENT_MODES.SPLIT && draftTotalPaidAmount <= 0) {
      return toast.error("Enter at least one payment amount");
    }

    setPaymentMode(paymentDraftMode);
    setCashAmount(draftCashPaymentAmount);
    setCardAmount(draftCardPaymentAmount);
    setPaymentRef(draftCardPaymentAmount > 0 ? paymentDraftRef : "");
    setPaymentTouched(true);
    setPaymentModalOpen(false);
  };

  const resetPaymentBreakdown = () => {
    setPaymentTouched(true);
    setPaymentMode(PAYMENT_MODES.CASH);
    setCashAmount(grandTotal);
    setCardAmount(0);
    setPaymentRef("");
  };

  const buildPaymentRequests = () => {
    const payments = [];

    if (cashPaymentAmount > 0) {
      payments.push({
        paymentMethod: "CASH",
        amount: cashPaymentAmount,
        referenceNumber: null,
        note: null,
      });
    }

    if (cardPaymentAmount > 0) {
      payments.push({
        paymentMethod: "CARD",
        amount: cardPaymentAmount,
        referenceNumber: paymentRef || null,
        note: null,
      });
    }

    return payments;
  };

  const processBarcodeScan = (barcodeValue) => {
    if (salesBlocked) {
      return toast.error(
        "Sales are closed for the business day. Please close your cash session."
      );
    }

    const scannedBarcode = normalizeBarcode(barcodeValue);

    if (!scannedBarcode || scannedBarcode.length < 4) {
      return;
    }

    const lookupBarcode = scannedBarcode.toLowerCase();

    const bottleProduct = config.bottlesEnabled ? bottles.find(
      (product) => normalizeBarcode(product.barcode).toLowerCase() === lookupBarcode
    ) : null;

    if (bottleProduct) {
      if (!hasAnyBottleStock(bottleProduct)) {
        return toast.error("Scanned product is out of stock");
      }

      /*
       * Barcode rule:
       * If measured sale is enabled for this bottle, scanning the barcode must
       * only open the sale type dialog. It must not add a full bottle directly
       * to the cart. The cashier will choose Full Bottle, 180 ml, or 90 ml from
       * the dialog.
       */
      if (bottleProduct.barProductType !== "CIGARETTE" && Boolean(bottleProduct.allowMeasureSale)) {
        setMeasureBottleModal(bottleProduct);
        return;
      }

      addItem(bottleProduct, "BOTTLE", BOTTLE_SALE_MODES.FULL_BOTTLE);
      return;
    }

    const restaurantProduct = bites.find(
      (product) => normalizeBarcode(product.barcode).toLowerCase() === lookupBarcode
    );

    if (restaurantProduct) {
      if (isRestaurantOutOfStock(restaurantProduct)) {
        return toast.error("Scanned item is out of stock");
      }

      addItem(restaurantProduct, resolveRestaurantItemType(restaurantProduct));
      return;
    }

    toast.error("Barcode not registered");
  };

  useEffect(() => {
    const flushBarcodeBuffer = () => {
      const barcodeValue = barcodeBufferRef.current;
      barcodeBufferRef.current = "";

      if (barcodeTimerRef.current) {
        clearTimeout(barcodeTimerRef.current);
        barcodeTimerRef.current = null;
      }

      processBarcodeScan(barcodeValue);
    };

    const isEditingInput = () => {
      const activeElement = document.activeElement;

      if (!activeElement) {
        return false;
      }

      const tagName = activeElement.tagName?.toLowerCase();

      return (
        activeElement.isContentEditable ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select"
      );
    };

    const handleGlobalBarcodeKeyDown = (event) => {
      if (
        customItemModalOpen ||
        paymentModalOpen ||
        measureBottleModal ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey ||
        isEditingInput()
      ) {
        return;
      }

      if (event.key === "Enter") {
        if (barcodeBufferRef.current) {
          event.preventDefault();
          event.stopPropagation();
          flushBarcodeBuffer();
        }

        return;
      }

      if (event.key.length !== 1) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      barcodeBufferRef.current += event.key;

      if (barcodeTimerRef.current) {
        clearTimeout(barcodeTimerRef.current);
      }

      barcodeTimerRef.current = setTimeout(() => {
        if (barcodeBufferRef.current) {
          flushBarcodeBuffer();
        }
      }, 120);
    };

    window.addEventListener("keydown", handleGlobalBarcodeKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleGlobalBarcodeKeyDown, true);

      if (barcodeTimerRef.current) {
        clearTimeout(barcodeTimerRef.current);
      }
    };
  }, [
    bottles,
    bites,
    cart,
    customItemModalOpen,
    paymentModalOpen,
    measureBottleModal,
    salesBlocked,
    config,
  ]);

  const submit = async () => {
    if (salesBlocked) {
      toast.error(
        "Sales are closed for the business day. Please close your cash session."
      );
      navigate(sessionPath);
      return;
    }

    if (!session) {
      toast.error("Open today's cash session first");
      navigate(sessionPath);
      return;
    }

    if (cart.length === 0) {
      return toast.error("Cart is empty");
    }

    if (grandTotal <= 0) {
      return toast.error("Grand total must be > 0");
    }

    if (totalPaidAmount <= 0) {
      return toast.error("Enter at least one payment amount");
    }

    if (!isPaymentBalanced) {
      return toast.error("Payment total must be equal to grand total");
    }

    setSubmitting(true);

    try {
      const sale = await PosService.createSale({
        clientRequestId: clientRequestIdRef.current,
        items: cart.map(
          ({
            itemType,
            itemId,
            quantity,
            barProductType,
            isReturnableBottle,
            returnedBottleQuantity,
            bottleSaleMode,
            customItem,
            customItemName,
            customUnitPrice,
          }) => ({
            itemType,
            itemId: customItem ? null : itemId,
            quantity,
            customItem: Boolean(customItem),
            customItemName: customItem ? customItemName : null,
            customUnitPrice: customItem
              ? Number(customUnitPrice || 0)
              : null,

            ...(itemType === "BOTTLE" && barProductType === "CIGARETTE"
              ? {}
              : {
                  returnedBottleQuantity:
                    itemType === "BOTTLE" && isReturnableBottle
                      ? Number(returnedBottleQuantity || 0)
                      : 0,
                  bottleReturned:
                    itemType === "BOTTLE" && isReturnableBottle
                      ? Number(returnedBottleQuantity || 0) > 0
                      : false,
                }),

            bottleSaleMode:
              itemType === "BOTTLE"
                ? bottleSaleMode || BOTTLE_SALE_MODES.FULL_BOTTLE
                : null,
          })
        ),

        payments: buildPaymentRequests(),

        discountAmount: appliedDiscount,
        serviceChargeEnabled: serviceEnabled,
        taxAmount: Number(tax || 0),
        cashierId: user.userId,
        cashierName: user.fullName,
      });

      toast.success(`Sale ${sale.saleCode || sale.billNumber} completed`);

      clear();
      clientRequestIdRef.current = createClientRequestId();

      loadCatalog();

      if (config.saleDetailRoute) navigate(config.saleDetailRoute(sale.saleId));
    } catch (e) {
      const responseMessage = e?.response?.data?.message;
      toast.error(
        typeof responseMessage === "string" && responseMessage.trim()
          ? responseMessage
          : "Unable to complete sale. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (sessionLoading) {
    return (
      <div
        className="grid place-items-center py-20 text-slate-500"
        data-testid="pos-session-loading"
      >
        Loading session…
      </div>
    );
  }

  if (!session) {
    return (
      <div
        className="max-w-xl mx-auto bms-card p-8 text-center"
        data-testid="pos-session-required"
      >
        <div className="h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 grid place-items-center mx-auto">
          <Lock className="h-7 w-7" />
        </div>

        <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">
          Open today's cash session
        </h2>

        <p className="text-slate-500 mt-2">
          You must open a cash session for{" "}
          {formatSriLankaDate(new Date(), { day: "2-digit", month: "short", year: "numeric" })}{" "}
          before creating any sale.
        </p>

        <Link to={sessionPath} data-testid="pos-go-cash-session">
          <Button className="mt-6 bg-navy-800 hover:bg-navy-900 text-white h-11 px-6">
            Open Cash Session
          </Button>
        </Link>
      </div>
    );
  }

  if (salesBlocked) {
    return (
      <div
        className="max-w-xl mx-auto bms-card p-8 text-center"
        data-testid="pos-sales-blocked"
      >
        <div className="h-14 w-14 rounded-2xl bg-rose-50 text-rose-600 grid place-items-center mx-auto">
          <Lock className="h-7 w-7" />
        </div>

        <h2 className="font-display text-2xl font-bold text-slate-900 mt-4">
          Sales are closed
        </h2>

        <p className="text-slate-500 mt-2">
          Sales are blocked after 2:10 AM until the next business day starts.
          Please close your cash session.
        </p>

        <Link to={sessionPath} data-testid="pos-go-close-session">
          <Button className="mt-6 bg-navy-800 hover:bg-navy-900 text-white h-11 px-6">
            Go to Cash Session
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_400px] gap-4 items-start lg:h-[calc(100dvh-115px)] lg:overflow-hidden">
        <div className="bms-card p-4 lg:p-5 lg:h-full min-h-0 overflow-hidden flex flex-col">
          <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-3 mb-4">
            <div className="w-full xl:w-auto overflow-x-auto pb-1"><div className="inline-flex min-w-max items-center gap-1 p-1 bg-slate-100 rounded-lg">
              {config.sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => { setTab(section.id); setSearch(""); }}
                  className={`px-4 py-2 rounded-md text-sm font-medium inline-flex items-center gap-2 ${
                    tab === section.id ? section.activeClass : "text-slate-600"
                  }`}
                  data-testid={`pos-tab-${section.id === "BOTTLE" ? "bar" : section.id === "FOOD" ? "food-bites" : section.id.toLowerCase()}`}
                >
                  {section.icon === "wine" ? <Wine className="h-4 w-4" /> : null}
                  {section.icon === "coffee" ? <Coffee className="h-4 w-4" /> : null}
                  {section.icon === "food" ? <Utensils className="h-4 w-4" /> : null}
                  {section.label}
                </button>
              ))}
            </div>

            </div>

            <div className="flex w-full xl:flex-1 flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3">
              <div className="relative w-full xl:max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />

                <Input
                  placeholder="Search all items…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="pos-search"
                />
              </div>

              {config.sections.find((section) => section.id === tab)?.customItemButton && (
                <Button
                  type="button"
                  onClick={openCustomItemModal}
                  className="h-10 shrink-0 bg-navy-800 hover:bg-navy-900 text-white font-semibold px-4"
                  data-testid="pos-custom-restaurant-item"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Custom Item
                </Button>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto pr-2">
            {loading ? (
              <div className="text-slate-500 py-12 text-center">
                Loading items…
              </div>
            ) : catalogError ? (
              <div className="py-12 text-center">
                <p className="text-rose-600">Couldn’t load the product catalog. Please try again.</p>
                <Button type="button" variant="outline" onClick={loadCatalog} className="mt-3">Retry</Button>
              </div>
            ) : bottles.length === 0 && bites.length === 0 ? (
              <div className="text-slate-500 py-12 text-center">The catalog is empty. No products are available.</div>
            ) : (
              <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                {filteredItems.map((product) => {
                  const isBottle = product.__posType === "BOTTLE";
                  const productTab = product.__posTab || tab;
                  const isBeverage = productTab === POS_TABS.BEVERAGE;

                  const id = isBottle ? product.bottleId : product.biteId;

                  const restaurantOut =
                    !isBottle && isRestaurantOutOfStock(product);

                  const out = isBottle
                    ? !hasAnyBottleStock(product)
                    : restaurantOut;

                  const low = isBottle
                    ? Number(product.availableQuantity || 0) > 0 &&
                      Number(product.availableQuantity || 0) <=
                        Number(product.lowStockLevel || 0)
                    : isRestaurantLowStock(product);

                  const stockStatusClass = out
                    ? "text-rose-600"
                    : low
                      ? "text-amber-600"
                      : "text-emerald-600";

                  const stockLabel = isBottle
                    ? out
                      ? "OUT"
                      : product.barProductType === "CIGARETTE"
                        ? `${Number(product.availableQuantity || 0)} units`
                      : product.allowMeasureSale
                        ? `${Number(product.availableQuantity || 0)} sealed · ${Number(
                            product.availableMeasureUnits || 0
                          )} units`
                        : `${Number(product.availableQuantity || 0)} sealed`
                    : product.stockTracked
                      ? out
                        ? "OUT"
                        : `${Number(product.availableQuantity || 0)} left`
                      : null;

                  return (
                    <button
                      key={`${product.__posType || productTab}-${id}`}
                      onClick={() => handleProductSelection(product, out)}
                      disabled={out}
                      className={`text-left bms-card p-3 sm:p-4 min-h-[220px] sm:min-h-[258px] hover:border-navy-400 hover:shadow-md transition group flex flex-col sm:flex-row items-stretch gap-3 sm:gap-4 ${
                        out ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                      data-testid={`pos-item-${productTab.toLowerCase()}-${id}`}
                    >
                      <ProductImageBox
                        product={product}
                        isBottle={isBottle}
                        isBeverage={isBeverage}
                      />

                      <div className="min-w-0 flex-1 py-1">
                        <div className="flex items-start justify-between gap-2 sm:gap-3">
                          <div className="min-w-0">
                            <div className="text-sm sm:text-base font-semibold text-slate-900 line-clamp-2 leading-snug">
                              {isBottle ? product.bottleName : product.biteName}
                            </div>

                            <div className="mt-0.5 text-xs sm:text-sm text-slate-500">
                              {isBottle
                                ? formatContainerAndVolume(product)
                                : product.biteCategoryName}
                            </div>
                          </div>

                          {stockLabel && (
                            <span
                              className={`shrink-0 rounded-full bg-white border border-slate-200 px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-[11px] font-semibold shadow-sm ${stockStatusClass}`}
                            >
                              {stockLabel}
                            </span>
                          )}
                        </div>

                        {isBottle && product.barProductType !== "CIGARETTE" && product.allowMeasureSale ? (
                          <div className="mt-2 space-y-0.5">
                            <div className="font-display font-semibold text-navy-800">
                              Bottle: {currency(product.sellingPrice)}
                            </div>

                            <div className="text-[11px] text-amber-700 font-semibold">
                              180 ml: {currency(product.measure180Price)}
                            </div>

                            <div className="text-[11px] text-amber-700 font-semibold">
                              90 ml: {currency(product.measure90Price)}
                            </div>

                            {product.isReturnableBottle && (
                              <div className="text-[11px] text-emerald-700 font-semibold">
                                Empty return credit:{" "}
                                {currency(product.bottleReturnCredit)}
                              </div>
                            )}

                            <div className="text-[10px] text-slate-500">
                              Open units:{" "}
                              {Number(product.availableMeasureUnits || 0)}
                            </div>
                          </div>
                        ) : isBottle && product.barProductType !== "CIGARETTE" && product.isReturnableBottle ? (
                          <div className="mt-2 space-y-0.5">
                            <div className="font-display font-semibold text-navy-800">
                              {currency(product.sellingPrice)}
                            </div>

                            <div className="text-[11px] text-emerald-700 font-semibold">
                              Empty return credit:{" "}
                              {currency(product.bottleReturnCredit)}
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 font-display font-semibold text-navy-800">
                            {currency(product.sellingPrice)}
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}

                {filteredItems.length === 0 && (
                  <div className="col-span-full text-center text-slate-400 py-8">
                    No items.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bms-card p-4 lg:p-5 lg:h-full lg:self-start min-h-0 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-3 shrink-0">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" /> Current bill
            </h3>

            {cart.length > 0 && (
              <button
                onClick={clear}
                className="text-xs text-slate-500 hover:text-rose-600 inline-flex items-center gap-1"
                data-testid="pos-clear"
              >
                <X className="h-3.5 w-3.5" /> Clear
              </button>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto -mx-2 px-2 divide-y divide-slate-100">
            {cart.length === 0 && (
              <div className="h-full min-h-[160px] grid place-items-center text-sm text-slate-400 text-center">
                Tap items on the left to add.
              </div>
            )}

            {cart.map((item) => {
              const itemAmounts = getCartItemAmounts(item);

              return (
                <div
                  key={item.key}
                  className="py-3 flex items-start gap-3"
                  data-testid={`cart-item-${item.key}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">
                      {item.name}
                    </div>

                    {item.customItem && (
                      <div className="text-xs text-purple-700 font-medium">
                        Custom restaurant sale
                      </div>
                    )}

                        {item.itemType === "BOTTLE" && item.barProductType !== "CIGARETTE" &&
                      item.isReturnableBottle && (
                        <div className="mt-2 max-w-[180px]">
                          <Label className="text-[10px] text-slate-500 uppercase tracking-wide">
                            Returned empty bottles
                          </Label>

                          <div className="mt-1 flex items-center gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                adjustReturnedBottleQuantity(item.key, -1)
                              }
                              disabled={
                                Number(item.returnedBottleQuantity || 0) <= 0
                              }
                              className="h-8 w-8 shrink-0 rounded-md border border-slate-200 grid place-items-center text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
                              data-testid={`returned-bottle-decr-${item.key}`}
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>

                            <Input
                              type="number"
                              min="0"
                              step="1"
                              value={item.returnedBottleQuantity ?? 0}
                              onChange={(e) =>
                                updateReturnedBottleQuantity(
                                  item.key,
                                  e.target.value
                                )
                              }
                              className="h-8 w-14 text-center"
                              data-testid={`returned-bottle-qty-${item.key}`}
                            />

                            <button
                              type="button"
                              onClick={() =>
                                adjustReturnedBottleQuantity(item.key, 1)
                              }
                              className="h-8 w-8 shrink-0 rounded-md border border-slate-200 grid place-items-center text-slate-600 hover:bg-slate-50"
                              data-testid={`returned-bottle-incr-${item.key}`}
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>

                          <div className="text-[11px] text-emerald-700 mt-1">
                            Credit:{" "}
                            {currency(
                              Number(item.returnedBottleQuantity || 0) *
                                Number(item.bottleReturnCredit || 0)
                            )}
                          </div>
                        </div>
                      )}

                    {item.itemType === "BOTTLE" &&
                      item.bottleSaleMode ===
                        BOTTLE_SALE_MODES.MEASURE_180ML && (
                        <div className="text-xs text-amber-700">
                          180 ml serving
                        </div>
                      )}

                    {item.itemType === "BOTTLE" &&
                      item.bottleSaleMode ===
                        BOTTLE_SALE_MODES.MEASURE_90ML && (
                        <div className="text-xs text-amber-700">
                          90 ml serving
                        </div>
                      )}

                    <div className="text-xs text-slate-500">
                      {currency(item.unitPrice)}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => decr(item.key)}
                      className="h-7 w-7 rounded-md border border-slate-200 grid place-items-center hover:bg-slate-50"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>

                    <span
                      className="w-6 text-center text-sm font-medium"
                      data-testid={`cart-qty-${item.key}`}
                    >
                      {item.quantity}
                    </span>

                    <button
                      onClick={() => incr(item.key)}
                      className="h-7 w-7 rounded-md border border-slate-200 grid place-items-center hover:bg-slate-50"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>

                  <div className="w-20 text-right font-medium text-sm">
                    <div>{currency(itemAmounts.netAmount)}</div>

                    {itemAmounts.returnCreditAmount > 0 && (
                      <div className="text-[10px] text-emerald-700 font-normal">
                        -{currency(itemAmounts.returnCreditAmount)}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => remove(item.key)}
                    className="text-slate-400 hover:text-rose-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="shrink-0 mt-auto pt-3 border-t border-slate-100 space-y-3">
            <div className="space-y-1.5 text-sm">
              <Row label="Sub total" value={currency(subTotal)} />

              <div className="grid grid-cols-3 gap-2 mt-1">
                <NumberInput
                  label="Discount"
                  value={discount}
                  onChange={setDiscount}
                />

                <NumberInput
                  label="Tax"
                  value={tax}
                  onChange={setTax}
                />

                <div>
                  <Label className="text-[10px] text-slate-500 uppercase tracking-wide">Service 10%</Label>
                  <button type="button" aria-pressed={serviceEnabled} onClick={() => setServiceEnabled((enabled) => !enabled)}
                    className={`h-9 w-full rounded-md border px-3 text-sm font-semibold ${serviceEnabled ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-input bg-background text-slate-600"}`}>
                    Apply
                  </button>
                </div>
              </div>

              <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="font-display font-semibold">Grand total</span>

                <span
                  className="font-display font-bold text-xl text-navy-900"
                  data-testid="pos-grand-total"
                >
                  {currency(grandTotal)}
                </span>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">
                    Payment
                  </div>
                  <div className="font-semibold text-slate-900">
                    {getPaymentModeLabel(paymentMode)}
                  </div>
                </div>

                <button
                  type="button"
                  onClick={openPaymentModal}
                  className="text-xs font-semibold text-navy-700 hover:underline"
                  data-testid="pos-payment-change"
                >
                  Change
                </button>
              </div>

              <div className="mt-2 space-y-1 border-t border-slate-200 pt-2 text-xs">
                {cashPaymentAmount > 0 && (
                  <Row label="Cash" value={currency(cashPaymentAmount)} />
                )}

                {cardPaymentAmount > 0 && (
                  <Row label="Card" value={currency(cardPaymentAmount)} />
                )}

                {paymentMode === PAYMENT_MODES.CARD && paymentRef && (
                  <Row label="Reference" value={paymentRef} />
                )}

                {paymentMode === PAYMENT_MODES.SPLIT && (
                  <Row
                    label="Balance"
                    value={currency(balanceAmount)}
                    valueClassName={
                      isPaymentBalanced ? "text-emerald-700" : "text-rose-600"
                    }
                  />
                )}
              </div>
            </div>

            {!isPaymentBalanced && (
              <div
                className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-medium text-rose-700"
                data-testid="pos-payment-mismatch"
              >
                Payment total must be equal to the grand total. Click Change to update payment.
              </div>
            )}

            <Button
              onClick={submit}
              disabled={salesBlocked || submitting || cart.length === 0}
              className="w-full h-12 bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
              data-testid="pos-submit"
            >
              {submitting
                ? "Processing…"
                : `Complete Sale · ${currency(grandTotal)}`}
            </Button>
          </div>
        </div>
      </div>

      {paymentModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90dvh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4 shrink-0">
              <div>
                <h3 className="font-display text-lg font-bold text-slate-900">
                  Select Payment
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Choose cash, card, or split payment for this bill.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setPaymentModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-4">
              <div className="grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => selectDraftPaymentMode(PAYMENT_MODES.CASH)}
                  className={`h-12 rounded-xl border text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                    paymentDraftMode === PAYMENT_MODES.CASH
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Banknote className="h-4 w-4" />
                  Cash
                </button>

                <button
                  type="button"
                  onClick={() => selectDraftPaymentMode(PAYMENT_MODES.CARD)}
                  className={`h-12 rounded-xl border text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                    paymentDraftMode === PAYMENT_MODES.CARD
                      ? "border-navy-500 bg-navy-50 text-navy-800"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <CreditCard className="h-4 w-4" />
                  Card
                </button>

                <button
                  type="button"
                  onClick={() => selectDraftPaymentMode(PAYMENT_MODES.SPLIT)}
                  className={`h-12 rounded-xl border text-xs font-semibold inline-flex items-center justify-center gap-1 ${
                    paymentDraftMode === PAYMENT_MODES.SPLIT
                      ? "border-amber-400 bg-amber-50 text-amber-700"
                      : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <Banknote className="h-4 w-4" />
                  Split
                </button>
              </div>

              {paymentDraftMode === PAYMENT_MODES.CASH && (
                <PaymentInfoBox
                  icon={<Banknote className="h-4 w-4 text-emerald-700" />}
                  title="Cash payment selected"
                  description="The full bill amount will be recorded as cash."
                />
              )}

              {paymentDraftMode === PAYMENT_MODES.CARD && (
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
                  <PaymentInfoBox
                    icon={<CreditCard className="h-4 w-4 text-sky-700" />}
                    title="Card payment selected"
                    description="The full bill amount will be recorded as card."
                  />

                  <Input
                    placeholder="Card reference (optional)"
                    value={paymentDraftRef}
                    onChange={(e) => setPaymentDraftRef(e.target.value)}
                    data-testid="pos-card-ref"
                  />
                </div>
              )}

              {paymentDraftMode === PAYMENT_MODES.SPLIT && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-3">
                  <div className="text-xs font-semibold text-amber-800">
                    Enter how much customer paid by cash and card.
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-600">
                        <Banknote className="h-4 w-4 text-emerald-600" />
                        Cash Received
                      </div>

                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentDraftCashAmount}
                        onChange={(e) => updateDraftCashAmount(e.target.value)}
                        className="h-9"
                        data-testid="pos-cash-amount"
                      />
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-slate-600">
                        <CreditCard className="h-4 w-4 text-sky-600" />
                        Card Amount
                      </div>

                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={paymentDraftCardAmount}
                        onChange={(e) => updateDraftCardAmount(e.target.value)}
                        className="h-9"
                        data-testid="pos-card-amount"
                      />
                    </div>
                  </div>

                  {draftCardPaymentAmount > 0 && (
                    <Input
                      placeholder="Card reference (optional)"
                      value={paymentDraftRef}
                      onChange={(e) => setPaymentDraftRef(e.target.value)}
                      data-testid="pos-card-ref"
                    />
                  )}
                </div>
              )}

              <div className="rounded-lg bg-white border border-slate-200 px-3 py-2 text-sm space-y-1">
                <Row label="Bill total" value={currency(grandTotal)} />
                <Row label="Total paid" value={currency(draftTotalPaidAmount)} />
                <Row
                  label="Balance"
                  value={currency(draftBalanceAmount)}
                  valueClassName={
                    isDraftPaymentBalanced ? "text-emerald-700" : "text-rose-600"
                  }
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 border-t border-slate-100 p-4 shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPaymentModalOpen(false)}
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={applyPaymentModal}
                className="bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
                data-testid="pos-payment-apply"
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}

      {customItemModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90dvh] overflow-hidden flex flex-col">
            <div className="px-5 pt-5 shrink-0">
              <h3 className="font-display text-lg font-bold text-slate-900">
                Add Custom Restaurant Item
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                Use this only for restaurant sales that are not available in the
                product list. This will not deduct stock.
              </p>
            </div>

            <div className="space-y-4 mt-5 px-5 overflow-y-auto">
              <div>
                <Label className="text-xs text-slate-500">Section</Label>

                <div className="grid grid-cols-3 gap-2 mt-1">
                  {Object.values(RESTAURANT_ITEM_SECTIONS).map((section) => (
                    <button
                      key={section}
                      type="button"
                      onClick={() => updateCustomItemForm("itemSection", section)}
                      className={`h-10 rounded-lg border text-xs font-semibold ${
                        customItemForm.itemSection === section
                          ? "bg-navy-50 border-navy-300 text-navy-800"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      }`}
                    >
                      {section}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs text-slate-500">Item name</Label>

                <Input
                  value={customItemForm.itemName}
                  onChange={(e) =>
                    updateCustomItemForm("itemName", e.target.value)
                  }
                  placeholder="Example: Extra chicken portion"
                  className="mt-1"
                  data-testid="custom-item-name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Unit price</Label>

                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={customItemForm.unitPrice}
                    onChange={(e) =>
                      updateCustomItemForm("unitPrice", e.target.value)
                    }
                    className="mt-1"
                    data-testid="custom-item-price"
                  />
                </div>

                <div>
                  <Label className="text-xs text-slate-500">Quantity</Label>

                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={customItemForm.quantity}
                    onChange={(e) =>
                      updateCustomItemForm("quantity", e.target.value)
                    }
                    className="mt-1"
                    data-testid="custom-item-quantity"
                  />
                </div>
              </div>

              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800">
                Custom restaurant items are saved as restaurant sales, but no
                stock batch will be deducted.
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 mt-5 border-t border-slate-100 p-5 shrink-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCustomItemModalOpen(false)}
              >
                Cancel
              </Button>

              <Button
                type="button"
                onClick={addCustomRestaurantItem}
                className="bg-gold-500 hover:bg-gold-600 text-navy-900 font-semibold"
                data-testid="custom-item-add"
              >
                Add to Bill
              </Button>
            </div>
          </div>
        </div>
      )}

      {measureBottleModal && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90dvh] overflow-hidden flex flex-col">
            <div className="p-5 overflow-y-auto">
              <h3 className="font-display text-lg font-bold text-slate-900">
                Select Sale Type
              </h3>

              <p className="text-sm text-slate-500 mt-1">
                {measureBottleModal.companyName} {measureBottleModal.bottleName}{" "}
                {formatContainerAndVolume(measureBottleModal)}
              </p>

              <div className="space-y-3 mt-5">
                <Button
                  type="button"
                  variant="outline"
                  disabled={Number(measureBottleModal.availableQuantity || 0) <= 0}
                  className="w-full h-auto justify-between px-4 py-3"
                  onClick={() =>
                    addItem(
                      measureBottleModal,
                      "BOTTLE",
                      BOTTLE_SALE_MODES.FULL_BOTTLE
                    )
                  }
                  data-testid="bottle-sale-full"
                >
                  <div className="text-left">
                    <div className="font-semibold">Full Bottle</div>

                    <div className="text-xs text-slate-500">
                      {Number(measureBottleModal.availableQuantity || 0)} sealed
                      available
                    </div>
                  </div>

                  <span className="font-semibold">
                    {currency(measureBottleModal.sellingPrice)}
                  </span>
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    getMeasuredMaximumQuantity(
                      measureBottleModal,
                      BOTTLE_SALE_MODES.MEASURE_180ML
                    ) <= 0
                  }
                  className="w-full h-auto justify-between px-4 py-3 border-amber-200 hover:bg-amber-50"
                  onClick={() =>
                    addItem(
                      measureBottleModal,
                      "BOTTLE",
                      BOTTLE_SALE_MODES.MEASURE_180ML
                    )
                  }
                  data-testid="bottle-sale-180"
                >
                  <div className="text-left">
                    <div className="font-semibold">180 ml Measure</div>

                    <div className="text-xs text-slate-500">
                      Uses 2 × 90 ml units
                    </div>
                  </div>

                  <span className="font-semibold text-amber-700">
                    {currency(measureBottleModal.measure180Price)}
                  </span>
                </Button>

                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    getMeasuredMaximumQuantity(
                      measureBottleModal,
                      BOTTLE_SALE_MODES.MEASURE_90ML
                    ) <= 0
                  }
                  className="w-full h-auto justify-between px-4 py-3 border-amber-200 hover:bg-amber-50"
                  onClick={() =>
                    addItem(
                      measureBottleModal,
                      "BOTTLE",
                      BOTTLE_SALE_MODES.MEASURE_90ML
                    )
                  }
                  data-testid="bottle-sale-90"
                >
                  <div className="text-left">
                    <div className="font-semibold">90 ml Measure</div>

                    <div className="text-xs text-slate-500">
                      Uses 1 measured unit
                    </div>
                  </div>

                  <span className="font-semibold text-amber-700">
                    {currency(measureBottleModal.measure90Price)}
                  </span>
                </Button>
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 mt-4 text-xs text-slate-600">
                <div>
                  Opened bottle units:{" "}
                  <span className="font-semibold">
                    {Number(measureBottleModal.availableMeasureUnits || 0)}
                  </span>
                </div>

                <div className="mt-1">
                  Sealed bottles:{" "}
                  <span className="font-semibold">
                    {Number(measureBottleModal.availableQuantity || 0)}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-5 border-t border-slate-100 shrink-0">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setMeasureBottleModal(null)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ProductImageBox = ({
  product,
  isBottle,
  isBeverage,
}) => {
  const [imageFailed, setImageFailed] = useState(false);

  const hasImage = Boolean(product.imageUrl) && !imageFailed;

  return (
    <div className="h-52 w-full sm:h-56 sm:w-44 md:w-48 shrink-0 rounded-xl border border-slate-200 bg-white p-2 overflow-hidden grid place-items-center">
      {hasImage ? (
        <img
          src={product.imageUrl}
          alt={isBottle ? product.bottleName : product.biteName}
          className="block max-h-full max-w-full object-contain"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <div className="h-full w-full rounded-lg bg-slate-50 grid place-items-center">
          <div
            className={`h-12 w-12 rounded-xl grid place-items-center ${
              isBottle
                ? "bg-rose-50 text-rose-700"
                : isBeverage
                  ? "bg-sky-50 text-sky-700"
                  : "bg-yellow-50 text-yellow-700"
            }`}
          >
            {isBottle ? (
              <Wine className="h-6 w-6" />
            ) : isBeverage ? (
              <Coffee className="h-6 w-6" />
            ) : (
              <Utensils className="h-6 w-6" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Row = ({ label, value, valueClassName = "" }) => (
  <div className="flex items-center justify-between gap-3">
    <span className="text-slate-500">{label}</span>
    <span className={`font-medium text-right break-words ${valueClassName}`}>
      {value}
    </span>
  </div>
);

const NumberInput = ({ label, value, onChange }) => (
  <div>
    <Label className="text-[10px] text-slate-500 uppercase tracking-wide">
      {label}
    </Label>

    <Input
      type="number"
      value={value}
      onChange={(e) =>
        onChange(e.target.value === "" ? "" : Number(e.target.value))
      }
      className="h-9"
    />
  </div>
);

const PaymentInfoBox = ({ icon, title, description }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-4 py-3">
    <div className="flex items-center gap-2">
      {icon}

      <div>
        <div className="text-xs font-semibold text-slate-800">
          {title}
        </div>

        <div className="text-[11px] text-slate-500 mt-0.5">
          {description}
        </div>
      </div>
    </div>
  </div>
);
